import { UserPrefsInput } from 'dtos/users/UserPrefsInput';
import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { createForm } from 'forms/userProfileForm';
import { Match } from 'navigo';
import { LoadStatus } from 'utils/types';
import { UserOutput } from 'dtos/users/UserOutput';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { AccountsStore } from 'stores/users/AccountsStore';

export class UserProfileStore {
  form = createForm();
  status: LoadStatus = 'none';
  message?: string;

  constructor(private readonly authStore: AuthStore, private readonly accountsStore: AccountsStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    this.status = 'none';
    this.message = undefined;

    this.accountsStore.ensureAccountsLoaded();
    const { fullName, email, jobRole, companyName, hasCostarLicense } = this.authStore.currentUser || {};

    this.form = createForm({ isInternal: !!this.authStore.isInternal });

    this.form.set('default', {
      fullName,
      email,
      // eslint-disable-next-line no-restricted-syntax
      jobRole: jobRole ? { title: jobRole } : null,
      // eslint-disable-next-line no-restricted-syntax
      companyName: companyName ? { title: companyName } : null,
      hasCostarLicense: !!hasCostarLicense,
    });
    this.form.reset();
  }

  async changeUserProfile() {
    this.status = 'loading';
    this.message = undefined;
    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid) {
      return;
    }
    const { fullName, email, jobRole: jobRoleRaw, companyName: companyNameRaw, hasCostarLicense } = this.form.values();
    const jobRole = jobRoleRaw?.title || '';
    const companyName = companyNameRaw?.title || '';

    const dto: UserPrefsInput = {
      fullName,
      email,
      jobRole,
      companyName,
      hasCostarLicense,
      profileComplete: !!(fullName && email && jobRole && companyName),
    };
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/user/prefs`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });
      if (!response.ok) throw new Error('Error changing profile');
      const user: UserOutput = nullsToUndefined(await response.json());
      runInAction(() => {
        this.status = 'success';
        this.message = 'Your profile was updated.';
      });
      if (!user.emailConfirmed) {
        this.authStore.forceLogout();
      } else {
        this.authStore.updateCurrentUser(user);
      }
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = 'Unable to change profile. Please try again.';
      });
    }
  }

  get accounts() {
    return this.accountsStore.accounts;
  }
}
